import { createElement, memo, useRef, useEffect } from '@max/max';
import View from '@hfe/max-view';
import Image from '@hfe/max-image';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { hotelTheme } from '@max/leez-theme-hotel';
import { setCustomTheme } from '@max/leez-context';
import { TechStackUtil } from '@nibfe/mkt-common-utils';
import { setRemConfig } from '@max/leez-style-util';
import Sensor from '@max/mbc-sensor';
import ExploreProgressV4 from './components/explore-progress-v4';
import RewardRecords from './components/reward-records';
import CityFilterModal from './components/city-filter-modal';
import { useCityExploreData, PopupKey } from './hooks/useCityExploreData';
import TaskListModal from './components/task-list-modal';
import { shouldShowEntry } from './utils/taskResolve';
import { ActivityStatus, ClickFreeCardUseBtnTriggerFlowType } from './types/shared';
import { BID_MAP, BID_TYPE, ASSIST_MODAL_VAP, FREE_CARD_USED_VAP, FREE_CARD_UNUSED_VAP, CITY_SELECTOR_CITY_VAP } from './const';
import './index.scss';
import GuestAssistModal from './components/guest-assist-modal';
import LandmarkDetailModal from './components/landmark-detail-modal';
import CouponModal from './components/coupon-modal';
import FreeCardModal from './components/free-card-modal';
import UnJoinedActivityBannerV2 from './components/unjoined-activity-banner-v2';
import PreJoinedCardV1 from './components/pre-joined-card-v1';
import FreeOrderMainCard from './components/free-order-main-card';
import FreeOrderReceivedCard from './components/free-order-received-card';
import ActivityEndedCard from './components/activity-ended-card';
import BindingRuleModal from './components/binding-rule-modal';
import WinnerListModal from './components/winner-list-modal';
import MockSceneSwitcher from './components/mock-scene-switcher';
import { preloadVap } from "./utils/vap-preload/index.web";
// 自测用户列表见 src/mock/users/index.js（CZ / LR / MOCK_USER_1 ...）
setCustomTheme(hotelTheme);
setRemConfig({
    enabled: true
});
preloadVap(ASSIST_MODAL_VAP);
preloadVap(FREE_CARD_USED_VAP);
preloadVap(FREE_CARD_UNUSED_VAP);
preloadVap(CITY_SELECTOR_CITY_VAP);
// ─── 前端自测用户身份 ──────────────────────────────────────────────────────────
// 自测时改为对应用户，如 MockUsers.LR，提交前必须改回 undefined
// leaderId 填入时模拟游客助力场景（被助力方的 userId）
const DEV_MOCK_USER = undefined;
const DEV_MOCK_LEADER_ID = undefined;
// 任务跳转自测：true 时注入下方 6 业务线 × 浏览/下单任务的运营配置（配合 mock 场景 TaskJumpTest，可用右下角场景切换面板切到「任务跳转自测」）。
// 因「无运营配置的任务不渲染」，mock 下发的任务必须在此有同 taskId 的配置项才会出现在弹窗里。提交前改回 false。
const DEV_MOCK_TASK_JUMP_TEST = false;
const DEV_TASK_JUMP_CONFIGS = [{
        taskId: '910011',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 酒店浏览
    {
        taskId: '910012',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    },
    // 酒店下单
    {
        taskId: '910041',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 民宿浏览
    {
        taskId: '910042',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    },
    // 民宿下单
    {
        taskId: '910051',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 门票浏览
    {
        taskId: '910052',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    },
    // 门票下单
    {
        taskId: '910061',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 度假浏览
    {
        taskId: '910062',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    },
    // 度假下单
    {
        taskId: '910021',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 机票浏览
    {
        taskId: '910022',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    },
    // 机票下单
    {
        taskId: '910031',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去浏览',
        finishedBtnText: '已完成'
    },
    // 火车票浏览
    {
        taskId: '910032',
        iconImage: 'https://img.meituan.net/makerjiulv/20fd2dd091c3e3406e50c720d96564394715.png',
        unfinishedBtnText: '去下单',
        finishedBtnText: '已完成'
    } // 火车票下单
];
// ─────────────────────────────────────────────────────────────────────────────
function CityExplore(props) {
    const ctx = useMonitorEngineContext();
    // 注入 mock 用户身份（仅开发调试，提交前必须将上方变量改回 undefined）
    if (DEV_MOCK_USER) {
        ctx._runtimeInfo.commonParams = {
            ...ctx._runtimeInfo.commonParams,
            userId: DEV_MOCK_USER.userId,
            token: DEV_MOCK_USER.token
        };
    }
    const { userId } = ctx._runtimeInfo.commonParams || {};
    const { leaderId } = ctx._runtimeInfo?.queryParams || {};
    const { activityStatus, viewModel, rawQueryInfoResponse, rawQueryRewardsResponse, freeOrder, popupMap, selectedLandmark, selectedRewardTabType, preSelectedCity, isSubscribed, skipCityModalAnimation, noticeItems, canBindBizLines, hasAssistRecord, cityExploreShare, handlePreSelectCity, handleConfirmJoin, handleReSelectCity, handleCloseAssistModal, handleClickLandmarkItem, handleCloseLandmarkDetailModal, handleCloseCouponModal, handleSelectRewardTabType, handleCloseRewardRecords, handleCloseFreeCardModal, handleCloseCityModal, handleJoinActivity, handleSubscribeNextActivity, handleOpenWinnerList, handleCloseWinnerList, handleOpenBindingRuleModal, handleCloseBindingRuleModal, taskCenter } = useCityExploreData({
        feConfig: DEV_MOCK_TASK_JUMP_TEST ? {
            ...props,
            taskCenterConfig: {
                ...props.taskCenterConfig,
                tasks: DEV_TASK_JUMP_CONFIGS
            }
        } : props,
        taskId: props.taskId
    });
    const currentActivityId = props.activityId;
    function lxReport(bid, bidType, extraVal) {
        try {
            const lxCommonParams = {
                activity_id: currentActivityId,
                activity_status: activityStatus,
                task_id: props.taskId,
                city_id: viewModel.currentCityId,
                city_name: viewModel.currentCityName,
                user_id: userId,
                is_guest_mode: !!leaderId,
                leader_id: leaderId,
                inviter_id: leaderId
            };
            const valLab = extraVal ? {
                ...lxCommonParams,
                ...extraVal
            } : lxCommonParams;
            switch (bidType) {
                case BID_TYPE.MV:
                    ctx._LXReport.mvReport(bid, {
                        valLab
                    });
                    break;
                case BID_TYPE.MC:
                    ctx._LXReport.mcReport(bid, {
                        valLab
                    });
                    break;
                case BID_TYPE.MC_WITH_TAG:
                    ctx._LXReport.mcReportWithTag(bid, {
                        valLab,
                        tagKey: bid,
                        tagContent: valLab
                    });
                    break;
                default:
                    break;
            }
        }
        catch (e) {
            ctx._raptorReport.reportLog('上报埋点异常', e, {
                category: 'jsError',
                level: 'warn',
                tags: {
                    bid
                }
            });
        }
    }
    function handleInviteClick() {
        lxReport(BID_MAP.INVITE_BUTTON_MC, BID_TYPE.MC);
        cityExploreShare();
    }
    useEffect(() => {
        if ([ActivityStatus.UNJOINED, ActivityStatus.PRE_JOINED, ActivityStatus.ON_PROGRESS, ActivityStatus.HAVE_FREE_CARD_UNUSED, ActivityStatus.HAVE_FREE_CARD_USED, ActivityStatus.ENDED].indexOf(activityStatus) > -1) {
            lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_MV, BID_TYPE.MV);
        }
    }, [activityStatus]);
    // 仅首屏加载展示骨架：首次离开 LOADING（首屏加载完成）后置 false，后续即使再回到 LOADING 也不展示
    const firstLoadingRef = useRef(true);
    if (activityStatus !== ActivityStatus.LOADING)
        firstLoadingRef.current = false;
    const showLoading = activityStatus === ActivityStatus.LOADING && firstLoadingRef.current;
    if (TechStackUtil.isInEditor()) {
        return <View style={{
            width: '750rpx',
            height: '504rpx',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
        <Image source={{
            uri: 'https://img.meituan.net/makerjiulv/69f5b253ce3755ff773bf520c4c87a9963842.jpg'
        }} resizeMode="contain" style={{
            width: '702rpx',
            height: '504rpx'
        }}/>
      </View>;
    }
    return <Sensor className="city-explore-root" once onAppear={() => {
        lxReport(BID_MAP.COMPONENT_MV, BID_TYPE.MV);
    }}>
      <View x-if={showLoading} className="city-explore-loading">
        <Image className="city-explore-loading" source={{
        uri: 'https://img.meituan.net/makerjiulv/41c03ed70ad7e8c2ce5e2b8bbd06cbc615547.png'
    }}/>
      </View>
      
      <UnJoinedActivityBannerV2 x-if={activityStatus === ActivityStatus.UNJOINED} onClickJoinButton={handleJoinActivity} feConfig={props} canBindBizLines={canBindBizLines} onOpenFreeCardRule={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_FREE_CARD_RULE_MC, BID_TYPE.MC);
        handleOpenBindingRuleModal();
    }} onJoinButtonShow={() => {
        lxReport(BID_MAP.INVITE_BUTTON_MV, BID_TYPE.MV);
    }}/>
      
      <PreJoinedCardV1 x-if={activityStatus === ActivityStatus.PRE_JOINED && !!preSelectedCity} feConfig={props} currentCityName={preSelectedCity?.name ?? ''} currentScore={viewModel.currentScore} totalScore={viewModel.totalScore} landmarks={viewModel.landmarks} noticeItems={noticeItems} onConfirmJoin={async () => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_CONFIRM_PARTICIPATE_MC, BID_TYPE.MC);
        handleConfirmJoin();
    }} onReSelect={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_RESELECT_CITY_MC, BID_TYPE.MC);
        handleReSelectCity();
    }} onOpenLandmark={(landmarkVo) => {
        lxReport(BID_MAP.LANDMARK_CLICK_MC, BID_TYPE.MC, {
            landmark_id: landmarkVo?.cardId
        });
        handleClickLandmarkItem(landmarkVo);
    }}/>
      
      <ExploreProgressV4 x-if={activityStatus === ActivityStatus.ON_PROGRESS} feConfig={props} currentCityName={viewModel.currentCityName} currentScore={viewModel.currentScore} totalScore={viewModel.totalScore} landmarks={viewModel.landmarks} hasAssistRecord={hasAssistRecord} noticeItems={noticeItems} onOpenFreeCardRule={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_FREE_CARD_RULE_MC, BID_TYPE.MC);
        handleOpenBindingRuleModal();
    }} onChangeTab={(tab) => {
        lxReport(BID_MAP.REWARD_BUTTON_MC, BID_TYPE.MC, {
            tab_id: tab
        });
        handleSelectRewardTabType(tab);
    }} onInviteButtonShow={() => {
        lxReport(BID_MAP.INVITE_BUTTON_MV, BID_TYPE.MV);
    }} onInviteClick={handleInviteClick} onOpenLandmark={(landmarkVo) => {
        lxReport(BID_MAP.LANDMARK_CLICK_MC, BID_TYPE.MC, {
            landmark_id: landmarkVo?.cardId
        });
        handleClickLandmarkItem(landmarkVo);
    }} showTaskEntry={shouldShowEntry(activityStatus, !!leaderId)} onTaskEntryShow={() => {
        lxReport(BID_MAP.TASK_ENTRY_MV, BID_TYPE.MV);
    }} onOpenTaskModal={() => {
        lxReport(BID_MAP.TASK_ENTRY_MC, BID_TYPE.MC);
        taskCenter.openTaskModal();
    }}/>
      
      <FreeOrderReceivedCard x-if={activityStatus === ActivityStatus.HAVE_FREE_CARD_UNUSED && (props.haveCardUnusedConfig?.compactLayout ?? true)} feConfig={props} rewardItem={freeOrder} onOpenBindingRule={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_FREE_CARD_RULE_MC, BID_TYPE.MC);
        handleOpenBindingRuleModal();
    }}/>
      
      <FreeOrderMainCard x-if={activityStatus === ActivityStatus.HAVE_FREE_CARD_UNUSED && !(props.haveCardUnusedConfig?.compactLayout ?? true)} feConfig={props} cityExploreViewModel={viewModel} noticeItems={noticeItems} rewardItem={freeOrder} onOpenBindingRule={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_FREE_CARD_RULE_MC, BID_TYPE.MC);
        handleOpenBindingRuleModal();
    }} onUseClick={({ cityInfo }) => {
        lxReport(BID_MAP.FREE_CARD_USE_MC, BID_TYPE.MC, {
            id: freeOrder?.rewardId
        });
        if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.JUMP_TO_ANCHOR && props.anchorKey?.trim()) {
            ctx._uiInteractionAPI?.anchor.scrollToAnchor({
                anchorKey: props.anchorKey.trim(),
                animated: true
            });
        }
        else if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.OPEN_BOTTOM_MODAL) {
            ctx?._communication?.emit?.('cityChanged', {
                value: cityInfo?.cityId || 0,
                title: cityInfo?.cityName || '全国'
            });
            ctx._communication?.emit('texBottomSlideModalStatusChange', {
                status: true,
                show: true,
                title: '货架'
            });
        }
    }}/>
      
      <FreeOrderMainCard x-if={activityStatus === ActivityStatus.HAVE_FREE_CARD_USED} feConfig={props} cityExploreViewModel={viewModel} noticeItems={noticeItems} rewardItem={freeOrder} onUseClick={({ cityInfo }) => {
        lxReport(BID_MAP.FREE_CARD_USE_MC, BID_TYPE.MC, {
            id: freeOrder?.rewardId
        });
        if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.JUMP_TO_ANCHOR && props.anchorKey?.trim()) {
            ctx._uiInteractionAPI?.anchor.scrollToAnchor({
                anchorKey: props.anchorKey.trim(),
                animated: true
            });
        }
        else if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.OPEN_BOTTOM_MODAL) {
            ctx?._communication?.emit?.('cityChanged', {
                value: cityInfo?.cityId || 0,
                title: cityInfo?.cityName || '全国'
            });
            ctx._communication?.emit('texBottomSlideModalStatusChange', {
                status: true,
                show: true,
                title: '货架'
            });
        }
    }}/>
      
      <ActivityEndedCard x-if={activityStatus === ActivityStatus.ENDED} isSubscribed={isSubscribed} feConfig={props} onSubscribe={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_EXPIRED_RESERVE_MC, BID_TYPE.MC);
        handleSubscribeNextActivity();
    }} onViewWinnerList={() => {
        lxReport(BID_MAP.CITY_EXPLORE_MAIN_CARD_EXPIRED_WINNER_LIST_MC, BID_TYPE.MC);
        handleOpenWinnerList();
    }}/>
      <CityFilterModal visible={popupMap[PopupKey.CITY_SELECT_MODAL]} cardGroupInfos={rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.cardGroupInfos ?? []} changeLimit={3} skipAnimation={skipCityModalAnimation} waterCityTagText={props.waterCityTagText} onClose={handleCloseCityModal} onOpened={() => {
        lxReport(BID_MAP.CITY_SELECTOR_MODAL_MV, BID_TYPE.MV);
    }} onSelectCity={(city) => {
        handlePreSelectCity(city);
        lxReport(BID_MAP.CITY_SELECT_MC, BID_TYPE.MC, {
            city_id: city.id,
            city_name: city.name
        });
    }} onCityCardShow={(city) => {
        lxReport(BID_MAP.CITY_SELECTOR_MV, BID_TYPE.MV, {
            city_id: city.id,
            city_name: city.name
        });
    }} onClickChangeButton={() => {
        lxReport(BID_MAP.REFRESH_CITIES_MC, BID_TYPE.MC);
    }} onClickSearchButton={() => {
        lxReport(BID_MAP.CITY_SEARCH_MC, BID_TYPE.MC);
    }}/>
      <GuestAssistModal visible={popupMap[PopupKey.GUEST_ASSIST_POPUP]} activityStatus={activityStatus} onClose={handleCloseAssistModal} onOpened={() => {
        lxReport(BID_MAP.ASSIST_MODAL_MV, BID_TYPE.MV);
    }} onClickJoin={() => {
        lxReport(BID_MAP.ASSIST_MODAL_JOIN_ACTIVITY_BUTTON_MC, BID_TYPE.MC);
    }}/>
      <LandmarkDetailModal visible={popupMap[PopupKey.LANDMARK_DETAIL_MODAL]} landmarkVO={selectedLandmark} onClose={handleCloseLandmarkDetailModal} children={props.children} onOpened={() => {
        lxReport(BID_MAP.LANDMARK_DETAIL_MV, BID_TYPE.MV);
    }}/>
      <CouponModal visible={popupMap[PopupKey.GET_COUPON_POPUP]} onClose={handleCloseCouponModal} onOpened={() => {
        lxReport(BID_MAP.COUPON_MODAL_MV, BID_TYPE.MV);
    }} rewardItemList={rawQueryRewardsResponse?.rewardItems ?? []}/>
      <FreeCardModal visible={popupMap[PopupKey.GET_FREE_ORDER_POPUP]} onClose={handleCloseFreeCardModal} onOpened={() => {
        lxReport(BID_MAP.FREE_CARD_MODAL_MV, BID_TYPE.MV, {
            id: freeOrder?.rewardId,
            amount: freeOrder?.amount
        });
    }} freeOrderCardVO={freeOrder} onClickUseButton={({ cityInfo }) => {
        lxReport(BID_MAP.FREE_CARD_USE_MC, BID_TYPE.MC, {
            id: freeOrder?.rewardId,
            amount: freeOrder?.amount
        });
        handleCloseFreeCardModal();
        if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.JUMP_TO_ANCHOR && props.anchorKey?.trim()) {
            ctx._uiInteractionAPI?.anchor.scrollToAnchor({
                anchorKey: props.anchorKey.trim(),
                animated: true
            });
        }
        else if (props.clickFreeCardUseBtnTriggerFlowType === ClickFreeCardUseBtnTriggerFlowType.OPEN_BOTTOM_MODAL) {
            ctx?._communication?.emit?.('cityChanged', {
                value: cityInfo?.cityId || 0,
                title: cityInfo?.cityName || '全国'
            });
            ctx._communication?.emit('texBottomSlideModalStatusChange', {
                status: true,
                show: true,
                title: '货架'
            });
        }
    }}/>
      <RewardRecords visible={popupMap[PopupKey.REWARD_RECORDS]} feConfig={props} activityId={currentActivityId} initialTab={selectedRewardTabType} onClose={handleCloseRewardRecords} upperLimit={viewModel.totalScore} taskInfos={rawQueryInfoResponse?.moduleInfoList?.find(m => m.taskListModuleInfo)?.taskListModuleInfo?.taskInfos ?? []} rankTime={{
        startTime: viewModel?.startTime,
        endTime: viewModel?.endTime
    }}/>
      
      <BindingRuleModal visible={popupMap[PopupKey.BINDING_RULE_MODAL]} ruleTitle={props.haveCardUnusedConfig?.bindingRuleTitle} ruleTitleColor={props.haveCardUnusedConfig?.bindingRuleTitleColor} ruleText={props.haveCardUnusedConfig?.bindingRuleText} onClose={handleCloseBindingRuleModal}/>
      
      <WinnerListModal visible={popupMap[PopupKey.WINNER_LIST_MODAL]} activityId={currentActivityId} headerImage={props.winnerListHeaderImage} onClose={handleCloseWinnerList}/>
      
      <TaskListModal visible={popupMap[PopupKey.TASK_LIST_MODAL]} taskCenterConfig={props.taskCenterConfig} taskList={taskCenter.taskListVo} onClose={taskCenter.closeTaskModal} onItemExpose={(task) => {
        lxReport(BID_MAP.TASK_ITEM_MV, BID_TYPE.MV, {
            task_item_id: task.taskId,
            task_type: task.taskType,
            biz_line: task.bizLine
        });
    }} onTaskClick={(task) => {
        lxReport(BID_MAP.TASK_ITEM_MC, BID_TYPE.MC, {
            task_item_id: task.taskId,
            task_type: task.taskType,
            biz_line: task.bizLine,
            button_state: task.buttonState
        });
        taskCenter.onTaskClick(task);
    }}/>
      
      <MockSceneSwitcher />
    </Sensor>;
}
export default memo(CityExplore);
